/**
 * Scantrust, the service behind most EU e-label QR codes: the QR is a viewer link carrying a uid
 * and an api_key, and the label itself comes from Scantrust's consumer API, which is the one
 * provider that sends CORS headers, so the phone reads it directly.
 */
import {
  buildELabel,
  ELabelParseError,
  emptyNutrition,
  type ELabelColour,
  type ELabelFacts,
  type ELabelNutrition,
} from "../facts.js";
import { withDefaultPort } from "./http.js";
import { stripHtml } from "../html.js";
import { singleStep, type ELabelProvider } from "./types.js";

/** What a scanned e-label QR carries and the consumer API needs. */
export interface ScantrustProof {
  uid: string;
  /** Percent-decoded. Secret-ish: memory only, never logged or stored. */
  apiKey: string;
}

export const SCANTRUST_API_HOST = "api.scantrust.com";
export const SCANTRUST_VIEWER_HOST = "elabel.scantrust.com";
const UID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Header-safe: visible ASCII only, so the key cannot smuggle a second header or a space. */
const API_KEY = /^[\x21-\x7e]{1,200}$/;

export function isScantrustUid(value: string): boolean {
  return UID.test(value);
}
export function isScantrustApiKey(value: string): boolean {
  return API_KEY.test(value);
}

/**
 * Reads uid and api_key from a scanned e-label QR (https://elabel.scantrust.com/default/#/?uid=…&api_key=…).
 * The host check is exact and https-only so a look-alike domain never receives a key; only the
 * fragment is read because that is where the viewer keeps them.
 */
export function parseScantrustElabelUrl(text: string): ScantrustProof | undefined {
  let url: URL;
  try {
    url = new URL(withDefaultPort(text));
  } catch {
    return undefined;
  }
  if (url.protocol !== "https:" || url.hostname !== SCANTRUST_VIEWER_HOST) return undefined;
  if (url.port !== "" || url.username !== "" || url.password !== "") return undefined;
  const queryStart = url.hash.indexOf("?");
  if (queryStart < 0) return undefined;
  const params = new Map<string, string>();
  for (const pair of url.hash.slice(queryStart + 1).split("&")) {
    const eq = pair.indexOf("=");
    if (eq < 0) continue;
    try {
      // decodeURIComponent, not URLSearchParams: a "+" in a key is a plus, not a space.
      params.set(pair.slice(0, eq), decodeURIComponent(pair.slice(eq + 1)));
    } catch {
      return undefined;
    }
  }
  const uid = params.get("uid");
  const apiKey = params.get("api_key");
  if (uid === undefined || apiKey === undefined) return undefined;
  if (!isScantrustUid(uid) || !isScantrustApiKey(apiKey)) return undefined;
  return { uid: uid.toLowerCase(), apiKey };
}

/** The one consumer endpoint e-labels come from; the uid is validated, so it cannot alter the path. */
export function scantrustCombinedInfoUrl(uid: string): string {
  if (!isScantrustUid(uid)) throw new ELabelParseError("uid is not a UUID");
  return `https://${SCANTRUST_API_HOST}/api/v2/consumer/scan/${uid.toLowerCase()}/combined-info/?show_elabel=true`;
}

export const SCANTRUST_KEY_HEADER = "X-ScanTrust-Consumer-Api-Key";

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- narrowed to a plain object just above.
      (value as Json)
    : {};
}
function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}
function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
/** A number or a numeric string ("2.09"); anything else is "not stated". */
function num(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && /^\s*-?\d+(?:[.,]\d+)?\s*$/.test(value)) {
    return Number(value.replace(",", "."));
  }
  return null;
}

function titleCase(text: string): string {
  return text
    .toLocaleLowerCase("en")
    .replace(
      /(^|[\s\-'’])(\p{L})/gu,
      (_, lead: string, letter: string) => lead + letter.toLocaleUpperCase("en"),
    );
}

function unique<T>(items: readonly T[]): T[] {
  return [...new Set(items)];
}

function parseColour(productInfos: Json): ELabelColour {
  const colour = str(productInfos["wine_colour"]).toLowerCase();
  for (const known of ["white", "red", "rose", "sparkling", "fortified"] as const) {
    if (colour.includes(known)) return known;
  }
  if (colour.includes("rosé")) return "rose";
  const type = str(productInfos["type"]).toLowerCase();
  if (type.includes("sparkling")) return "sparkling";
  if (type.includes("fortified")) return "fortified";
  return "unknown";
}

function parseGrapes(value: unknown): string[] {
  const raw = Array.isArray(value) ? value.map(str).join(",") : str(value);
  return unique(
    raw
      .split(/[,/;&]/)
      .map((grape) => titleCase(grape.replace(/\s+/g, " ").trim()))
      .filter((grape) => grape !== ""),
  );
}

/** "ingredient_malic_acid" -> "Malic acid"; text without the key prefix is the producer's own and kept. */
function decodeIngredient(key: string): string {
  const trimmed = key.trim();
  if (!trimmed.startsWith("ingredient_")) return trimmed;
  const words = trimmed.slice("ingredient_".length).replace(/_/g, " ").trim();
  return words === "" ? "" : words.charAt(0).toUpperCase() + words.slice(1);
}

function parseNutrition(table: unknown[]): ELabelNutrition {
  const out: ELabelNutrition = emptyNutrition();
  const grams: Readonly<Record<string, keyof ELabelNutrition>> = {
    fat: "fatG",
    saturated_fat: "saturatedFatG",
    carbohydrate: "carbohydrateG",
    sugar: "sugarG",
    protein: "proteinG",
    salt: "saltG",
  };
  const visit = (row: Json): void => {
    const key = str(row["key"]);
    const unit = str(row["unit"]).toLowerCase();
    const value = num(row["per_100ml"]);
    if (unit === "kj") out.energyKj = value;
    else if (unit === "kcal") out.energyKcal = value;
    else {
      const field = grams[key];
      if (field !== undefined && unit === "g") out[field] = value;
    }
    for (const child of arr(row["children"])) visit(obj(child));
  };
  for (const row of table) visit(obj(row));
  return out;
}

function parseServingMl(servingInfos: Json): number | null {
  const amount = num(obj(servingInfos["serving"])["default"]);
  if (amount === null || amount <= 0) return null;
  const unit = str(obj(servingInfos["unit"])["default"]).toLowerCase();
  if (unit === "cl") return amount * 10;
  return unit === "" || unit === "ml" ? amount : null;
}

/**
 * Turns a Scantrust `combined-info` response into our e-label shape. Missing or malformed
 * sections become empty or `null`, never a guess; it throws only when the input is not an
 * e-label with a SKU at all, because such a label could not be told apart from another.
 */
export function parseScantrustCombinedInfo(json: unknown): ELabelFacts {
  const root = obj(json);
  const product = obj(obj(root["code"])["product"]);
  const sku = str(product["sku"]);
  if (sku === "") throw new ELabelParseError("no product SKU");
  const published = obj(obj(root["elabel"])["published"]);
  if (Object.keys(published).length === 0) throw new ELabelParseError("no published e-label");

  const infos = obj(published["product-infos"]);
  const ingredients = obj(published["ingredients"]);
  const ingredientList = arr(ingredients["list"]).map(obj);
  const freeText: Record<string, string> = {};
  for (const [lang, html] of Object.entries(obj(ingredients["free_text"]))) {
    const text = stripHtml(str(html));
    if (text !== "" && /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})?$/.test(lang)) {
      freeText[lang.toLowerCase()] = text;
    }
  }
  const vintage = num(infos["vintage_year"]);
  const abv = num(infos["alcohol_volume"]);
  const updatedAt = str(obj(root["elabel"])["updated_at"]);

  return buildELabel("scantrust", sku, {
    productName: str(product["name"]),
    producerName: str(obj(root["company"])["name"]) || str(infos["company_name"]),
    originCountries: unique(
      arr(infos["origin"])
        .map((c) => str(c).toUpperCase())
        .filter((c) => /^[A-Z]{2}$/.test(c)),
    ),
    colour: parseColour(infos),
    grapes: parseGrapes(infos["vine_variety"]),
    vintage:
      vintage !== null && Number.isInteger(vintage) && vintage >= 1800 && vintage <= 2100
        ? vintage
        : null,
    abvPercent: abv !== null && abv >= 0 && abv <= 100 ? abv : null,
    ingredients: {
      items: unique(
        ingredientList.map((i) => decodeIngredient(str(i["description"]))).filter((i) => i !== ""),
      ),
      freeText,
    },
    nutritionPer100ml: parseNutrition(arr(obj(published["nutrition"])["table"])),
    allergens: unique(
      ingredientList
        .filter((i) => i["is_allergen"] === true)
        .map((i) => decodeIngredient(str(i["description"])))
        .filter((i) => i !== ""),
    ),
    servingMl: parseServingMl(obj(published["serving-infos"])),
    sourceUpdatedAt: updatedAt === "" ? null : updatedAt,
  });
}

/** The viewer link for a proof, rebuilt from its checked parts: also what a Scantrust upload proves with. */
export function scantrustViewerUrl(proof: ScantrustProof): string {
  return `https://${SCANTRUST_VIEWER_HOST}/default/#/?uid=${proof.uid}&api_key=${encodeURIComponent(proof.apiKey)}`;
}

/** A Scantrust consumer response from a step's text; anything else is not this provider's label. */
export function parseScantrustText(text: string): ELabelFacts {
  return parseScantrustCombinedInfo(JSON.parse(text));
}

/**
 * The one provider whose data endpoint sends CORS headers, so the phone fetches it directly. The
 * key travels in a header, never in a URL, and a redirect is never followed.
 */
export const scantrustProvider: ELabelProvider = {
  id: "scantrust",
  provider: "scantrust",
  name: "Scantrust",
  state: "live",
  hosts: [SCANTRUST_VIEWER_HOST, SCANTRUST_API_HOST],
  match(text) {
    const proof = parseScantrustElabelUrl(text);
    if (proof === undefined) return undefined;
    return {
      provider: "scantrust",
      url: scantrustViewerUrl(proof),
      ref: "",
      params: { uid: proof.uid, apiKey: proof.apiKey },
    };
  },
  needs: { userAgent: "honest" },
  steps: [
    {
      id: "combined-info",
      kind: "fetch",
      access: "cors",
      request: ({ target }) => ({
        method: "GET",
        url: scantrustCombinedInfoUrl(target.params["uid"] ?? ""),
        headers: {
          [SCANTRUST_KEY_HEADER]: target.params["apiKey"] ?? "",
          accept: "application/json",
        },
      }),
    },
  ],
  parse: singleStep("combined-info", (text) => parseScantrustText(text)),
};
